import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Truck,
  Package,
  Plus,
  CheckCircle2,
  XCircle,
  Clock,
  Search,
  AlertCircle,
  RotateCcw,
  Paperclip,
  Ban,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { DatePicker } from "@/components/ui/DatePicker";
import { PageLoader } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";
import { formatDate } from "@/utils/formatDate";
import {
  useCourierRequests,
  useCreateCourierRequest,
  useOrderCourierRequest,
  useRejectCourierRequest,
  useReopenCourierRequest,
  useMarkReceivedCourierRequest,
  useCancelCourierRequest,
} from "../hooks/useCouriers";
import {
  COURIER_STATUS_COLORS,
  PICKUP_CITY_SUGGESTIONS,
  type CourierRequest,
  type CourierStatus,
} from "../types";

type StatusFilter = "all" | CourierStatus;

const STATUS_TABS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "Wszystkie" },
  { value: "pending", label: "Oczekujące" },
  { value: "ordered", label: "Zamówione" },
  { value: "rejected", label: "Odrzucone" },
  { value: "cancelled", label: "Anulowane" },
];

export function CouriersPage() {
  const user = useAuthStore((s) => s.user);
  const isManager = !!user?.has_small_account_access;
  const qc = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");

  const params: Record<string, string> = {};
  if (statusFilter !== "all") params.status = statusFilter;
  if (search.trim()) params.search = search.trim();

  const { data: items = [], isLoading } = useCourierRequests(
    Object.keys(params).length > 0 ? params : undefined
  );

  const pendingCount = items.filter((i) => i.status === "pending").length;
  // A non-manager requester needs the actions column to cancel their own pending request.
  const showActionsColumn =
    isManager || items.some((i) => i.status === "pending" && i.requested_by === user?.id);

  // ── Create modal ──────────────────────────────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    delivery_type: "courier" as "courier" | "parcel_locker",
    pickup_address: "",
    size: "",
    recipient_address: "",
    notes: "",
    requested_at: "",
  });
  const [createErrors, setCreateErrors] = useState<Record<string, string>>({});
  const [pickupSuggestionsOpen, setPickupSuggestionsOpen] = useState(false);
  const createItem = useCreateCourierRequest();

  const resetForm = () => {
    setForm({
      delivery_type: "courier",
      pickup_address: "",
      size: "",
      recipient_address: "",
      notes: "",
      requested_at: "",
    });
    setCreateErrors({});
    setPickupSuggestionsOpen(false);
  };

  const submitCreate = () => {
    const errors: Record<string, string> = {};
    if (!form.pickup_address.trim()) errors.pickup_address = "Adres nadania jest wymagany";
    if (!form.size.trim()) errors.size = "Rozmiar jest wymagany";
    if (!form.recipient_address.trim())
      errors.recipient_address =
        form.delivery_type === "parcel_locker"
          ? "Paczkomat odbiorcy jest wymagany"
          : "Adres odbiorcy jest wymagany";
    if (Object.keys(errors).length > 0) {
      setCreateErrors(errors);
      return;
    }
    createItem.mutate(
      {
        delivery_type: form.delivery_type,
        pickup_address: form.pickup_address.trim(),
        size: form.size.trim(),
        recipient_address: form.recipient_address.trim(),
        notes: form.notes.trim(),
        requested_at: form.requested_at || undefined,
      },
      {
        onSuccess: () => {
          setCreateOpen(false);
          resetForm();
        },
        onError: () => {
          setCreateErrors({ submit: "Błąd podczas dodawania. Spróbuj ponownie." });
        },
      }
    );
  };

  // ── Order modal ───────────────────────────────────────────────────────────
  const [orderModal, setOrderModal] = useState<CourierRequest | null>(null);
  const [orderForm, setOrderForm] = useState({ ordered_at: "", ordered_for: "" });
  const [labelFile, setLabelFile] = useState<File | null>(null);
  const [orderError, setOrderError] = useState("");
  const orderItem = useOrderCourierRequest();

  const openOrderModal = (item: CourierRequest) => {
    setOrderModal(item);
    setOrderForm({ ordered_at: "", ordered_for: "" });
    setLabelFile(null);
    setOrderError("");
  };

  const submitOrder = () => {
    if (!orderModal) return;
    setOrderError("");
    orderItem.mutate(
      {
        id: orderModal.id,
        data: {
          ordered_at: orderForm.ordered_at || undefined,
          ordered_for: orderForm.ordered_for || undefined,
          label_file: labelFile ?? undefined,
        },
      },
      {
        onSuccess: () => setOrderModal(null),
        onError: (err: unknown) => {
          // Surface the backend's reason (e.g. the request was cancelled in the meantime)
          // and refresh the list so a stale frontend reflects the real current state.
          const detail = (err as { response?: { data?: { errors?: { detail?: string } } } })
            ?.response?.data?.errors?.detail;
          setOrderError(detail || "Błąd podczas zamawiania. Spróbuj ponownie.");
          qc.invalidateQueries({ queryKey: ["courier-requests"] });
        },
      }
    );
  };

  // ── Reject modal ──────────────────────────────────────────────────────────
  const [rejectModal, setRejectModal] = useState<CourierRequest | null>(null);
  const [rejectNotes, setRejectNotes] = useState("");
  const [rejectError, setRejectError] = useState("");
  const rejectItem = useRejectCourierRequest();

  const openRejectModal = (item: CourierRequest) => {
    setRejectModal(item);
    setRejectNotes(item.notes);
    setRejectError("");
  };

  const submitReject = () => {
    if (!rejectModal) return;
    setRejectError("");
    rejectItem.mutate(
      { id: rejectModal.id, data: { notes: rejectNotes } },
      {
        onSuccess: () => setRejectModal(null),
        onError: () => setRejectError("Błąd podczas odrzucania. Spróbuj ponownie."),
      }
    );
  };

  // ── Mark received / Cancel ────────────────────────────────────────────────
  const markReceived = useMarkReceivedCourierRequest();
  const reopenItem = useReopenCourierRequest();
  const cancelItem = useCancelCourierRequest();

  const handleCancel = (item: CourierRequest) => {
    if (window.confirm(`Czy na pewno chcesz anulować zgłoszenie #${item.number}?`)) {
      cancelItem.mutate(item.id);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4 sm:mb-5">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">Zamawianie kuriera</h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-3 sm:mb-6" />
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            Zgłaszaj i śledź zapotrzebowanie na przesyłki kurierskie i paczkomatowe.
          </p>
        </div>
        <Button
          onClick={() => { resetForm(); setCreateOpen(true); }}
          className="flex-shrink-0"
          aria-label="Nowe zgłoszenie"
        >
          <Plus size={15} className="sm:mr-1.5" />
          <span className="hidden sm:inline">Nowe zgłoszenie</span>
        </Button>
      </div>

      {/* Pending info bar */}
      {pendingCount > 0 && statusFilter === "all" && (
        <div className="mb-4 flex items-start sm:items-center gap-2 text-xs sm:text-sm bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg px-3 sm:px-4 py-2 sm:py-2.5">
          <AlertCircle size={15} className="text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5 sm:mt-0" />
          <span className="text-yellow-800 dark:text-yellow-300 font-medium">
            {pendingCount} {pendingCount === 1 ? "zgłoszenie oczekuje" : "zgłoszeń oczekuje"} na realizację
            {isManager ? " — do zamówienia przez Ciebie" : ""}
          </span>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row md:flex-wrap md:items-center gap-2 md:gap-3 mb-4 md:mb-5">
        <div className="-mx-4 px-4 md:mx-0 md:px-0 overflow-x-auto scrollbar-hide">
          <div className="inline-flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 whitespace-nowrap">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setStatusFilter(tab.value)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  statusFilter === tab.value
                    ? "bg-white dark:bg-gray-700 text-[var(--color-primary)] shadow-sm"
                    : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="relative flex-1 md:min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Szukaj po adresie odbiorcy..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-2 md:py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 placeholder-gray-400"
          />
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <PageLoader />
      ) : items.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 p-10 text-center">
          <Truck size={36} className="text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400 dark:text-gray-500 text-sm">Brak zgłoszeń w tej kategorii.</p>
          <button
            onClick={() => { resetForm(); setCreateOpen(true); }}
            className="mt-3 text-sm text-[var(--color-primary)] dark:text-blue-400 hover:underline"
          >
            Dodaj pierwsze zgłoszenie
          </button>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 overflow-hidden">
          {/* Mobile: card list */}
          <ul className="md:hidden flex flex-col gap-2">
            {items.map((item) => (
              <CourierCard
                key={item.id}
                item={item}
                isManager={isManager}
                currentUserId={user?.id ?? null}
                onOrder={openOrderModal}
                onReject={openRejectModal}
                onMarkReceived={(i) => markReceived.mutate(i.id)}
                onReopen={(i) => reopenItem.mutate(i.id)}
                onCancel={handleCancel}
              />
            ))}
          </ul>

          {/* Desktop: table */}
          <div className="hidden md:block bg-white dark:bg-gray-800 overflow-x-auto">
            <table className="w-full text-xs min-w-[900px]">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80">
                  <th className="text-left px-3 py-2 w-24 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Data</th>
                  <th className="text-left px-3 py-2 w-28 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Typ</th>
                  <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Adres nadania</th>
                  <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Adres odbiorcy</th>
                  <th className="text-left px-3 py-2 w-24 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Rozmiar</th>
                  <th className="text-left px-3 py-2 w-28 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Zgłosił</th>
                  <th className="text-left px-3 py-2 w-36 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Status</th>
                  <th className="text-left px-3 py-2 w-24 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Zamówiono</th>
                  <th className="text-left px-3 py-2 w-24 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Zamówiono na</th>
                  <th className="text-left px-3 py-2 w-20 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Odebrana</th>
                  {showActionsColumn && (
                    <th className="text-left px-3 py-2 w-36 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Akcje</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {items.map((item) => (
                  <CourierRow
                    key={item.id}
                    item={item}
                    isManager={isManager}
                    showActions={showActionsColumn}
                    currentUserId={user?.id ?? null}
                    onOrder={openOrderModal}
                    onReject={openRejectModal}
                    onMarkReceived={(i) => markReceived.mutate(i.id)}
                    onReopen={(i) => reopenItem.mutate(i.id)}
                    onCancel={handleCancel}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-1 md:px-4 py-2.5 md:border-t border-gray-100 dark:border-gray-700 text-xs text-gray-400 dark:text-gray-500">
            {items.length} {items.length === 1 ? "zgłoszenie" : "zgłoszeń"}
          </div>
        </div>
      )}

      {/* ── Create modal ── */}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Nowe zgłoszenie kuriera">
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="col-span-1 sm:col-span-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Typ przesyłki <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-3">
                {(["courier", "parcel_locker"] as const).map((type) => (
                  <label
                    key={type}
                    className={`flex-1 flex items-center justify-center gap-2 rounded-md border-2 py-2.5 text-sm font-medium cursor-pointer transition-colors ${
                      form.delivery_type === type
                        ? "border-[var(--color-primary)] bg-blue-50 dark:bg-blue-900/20 text-[var(--color-primary)] dark:text-blue-300"
                        : "border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500"
                    }`}
                  >
                    <input
                      type="radio"
                      className="sr-only"
                      value={type}
                      checked={form.delivery_type === type}
                      onChange={() => setForm((f) => ({ ...f, delivery_type: type, pickup_address: "" }))}
                    />
                    {type === "courier" ? <Truck size={16} /> : <Package size={16} />}
                    {type === "courier" ? "Kurier" : "Paczkomat"}
                  </label>
                ))}
              </div>
            </div>

            <div className="col-span-1 sm:col-span-2 relative">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Adres nadania <span className="text-red-500">*</span>
              </label>
              <textarea
                rows={2}
                autoComplete="off"
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm resize-y"
                placeholder="np. Biuro Wrocław, Biuro Opole"
                value={form.pickup_address}
                onChange={(e) => {
                  setForm((f) => ({ ...f, pickup_address: e.target.value }));
                  setPickupSuggestionsOpen(true);
                }}
                onFocus={() => setPickupSuggestionsOpen(true)}
                onBlur={() => setTimeout(() => setPickupSuggestionsOpen(false), 150)}
              />
              {/* Autocomplete dropdown */}
              {pickupSuggestionsOpen && (
                <ul className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg text-sm overflow-hidden">
                  {PICKUP_CITY_SUGGESTIONS.filter(
                    (s) => !form.pickup_address.trim() || s.toLowerCase().includes(form.pickup_address.trim().toLowerCase())
                  ).map((suggestion) => (
                    <li
                      key={suggestion}
                      className="px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer text-gray-700 dark:text-gray-200"
                      onMouseDown={() => {
                        setForm((f) => ({ ...f, pickup_address: suggestion }));
                        setPickupSuggestionsOpen(false);
                      }}
                    >
                      {suggestion}
                    </li>
                  ))}
                </ul>
              )}
              {createErrors.pickup_address && (
                <p className="text-xs text-red-500 mt-1">{createErrors.pickup_address}</p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Rozmiar <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                placeholder="np. mała, duża, koperta"
                value={form.size}
                onChange={(e) => setForm((f) => ({ ...f, size: e.target.value }))}
              />
              {createErrors.size && (
                <p className="text-xs text-red-500 mt-1">{createErrors.size}</p>
              )}
              {form.delivery_type === "parcel_locker" && (
                <a
                  href="https://inpost.pl/cenniki"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-[var(--color-primary)] dark:text-blue-400 hover:underline mt-1 inline-block"
                >
                  Cennik InPost
                </a>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Odbiór nie wcześniej niż <span className="text-gray-400 text-xs">(opcjonalnie)</span>
              </label>
              <DatePicker
                value={form.requested_at}
                onChange={(v) => setForm((f) => ({ ...f, requested_at: v }))}
              />
            </div>

            <div className="col-span-1 sm:col-span-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                {form.delivery_type === "parcel_locker" ? "Paczkomat odbiorcy" : "Adres odbiorcy"}{" "}
                <span className="text-red-500">*</span>
              </label>
              <textarea
                rows={3}
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm resize-y"
                placeholder={
                  form.delivery_type === "parcel_locker"
                    ? "np. WRO01M\nul. Przykładowa 1\n00-000 Wrocław"
                    : "Imię Nazwisko\nul. Przykładowa 1\n00-000 Miasto"
                }
                value={form.recipient_address}
                onChange={(e) => setForm((f) => ({ ...f, recipient_address: e.target.value }))}
              />
              {createErrors.recipient_address && (
                <p className="text-xs text-red-500 mt-1">{createErrors.recipient_address}</p>
              )}
            </div>

            <div className="col-span-1 sm:col-span-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Uwagi <span className="text-gray-400 text-xs">(opcjonalnie)</span>
              </label>
              <textarea
                rows={2}
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                placeholder="Dodatkowe informacje..."
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </div>
          </div>

          {createErrors.submit && (
            <p className="text-sm text-red-600">{createErrors.submit}</p>
          )}

          <div className="flex gap-3">
            <Button loading={createItem.isPending} onClick={submitCreate}>
              Dodaj zgłoszenie
            </Button>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              Anuluj
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Order modal ── */}
      <Modal open={!!orderModal} onClose={() => setOrderModal(null)} title="Zamów przesyłkę">
        {orderModal && (
          <div className="flex flex-col gap-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3 text-sm">
              <p className="font-semibold text-[var(--color-primary)] dark:text-blue-200">
                {orderModal.delivery_type_display}
              </p>
              <p className="text-[var(--color-primary)] dark:text-blue-400 text-xs mt-0.5">
                {orderModal.pickup_address} → {orderModal.recipient_address}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Rozmiar: {orderModal.size} · Zgłosił: {orderModal.requested_by_name ?? "—"}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Data zamówienia <span className="text-gray-400 text-xs">(domyślnie: dziś)</span>
              </label>
              <DatePicker
                value={orderForm.ordered_at}
                onChange={(v) => setOrderForm((f) => ({ ...f, ordered_at: v }))}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Zamówiono na <span className="text-gray-400 text-xs">(opcjonalnie)</span>
              </label>
              <DatePicker
                value={orderForm.ordered_for}
                onChange={(v) => setOrderForm((f) => ({ ...f, ordered_for: v }))}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">
                Skan etykiety <span className="text-gray-400 text-xs">(opcjonalnie)</span>
              </label>
              <FileDropZone
                files={labelFile ? [labelFile] : []}
                onFilesChange={(fs) => setLabelFile(fs[0] ?? null)}
                accept="image/*,application/pdf"
                label="Przeciągnij plik lub kliknij, aby wybrać (PDF lub zdjęcie)"
              />
            </div>

            {orderError && <p className="text-sm text-red-600">{orderError}</p>}

            <div className="flex gap-3">
              <Button loading={orderItem.isPending} onClick={submitOrder}>
                <CheckCircle2 size={14} className="mr-1.5" /> Zamów
              </Button>
              <Button variant="secondary" onClick={() => setOrderModal(null)}>
                Anuluj
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Reject modal ── */}
      <Modal open={!!rejectModal} onClose={() => setRejectModal(null)} title="Odrzuć zgłoszenie">
        {rejectModal && (
          <div className="flex flex-col gap-4">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 text-sm">
              <p className="font-semibold text-red-900 dark:text-red-200">
                #{rejectModal.number} {rejectModal.delivery_type_display}
              </p>
              <p className="text-red-700 dark:text-red-400 text-xs mt-0.5">
                {rejectModal.requested_by_name ?? "—"} · {rejectModal.recipient_address}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Uwagi / Powód odrzucenia <span className="text-gray-400 text-xs">(opcjonalnie)</span>
              </label>
              <textarea
                rows={2}
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                placeholder="Powód odrzucenia..."
                value={rejectNotes}
                onChange={(e) => setRejectNotes(e.target.value)}
              />
            </div>

            {rejectError && <p className="text-sm text-red-600">{rejectError}</p>}

            <div className="flex gap-3">
              <Button loading={rejectItem.isPending} onClick={submitReject} variant="secondary">
                <XCircle size={14} className="mr-1.5" /> Odrzuć
              </Button>
              <Button variant="secondary" onClick={() => setRejectModal(null)}>
                Anuluj
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ── Shared props ──────────────────────────────────────────────────────────────

interface CourierItemProps {
  item: CourierRequest;
  isManager: boolean;
  currentUserId: string | null;
  onOrder: (item: CourierRequest) => void;
  onReject: (item: CourierRequest) => void;
  onMarkReceived: (item: CourierRequest) => void;
  onReopen: (item: CourierRequest) => void;
  onCancel: (item: CourierRequest) => void;
}

// ── Table row ─────────────────────────────────────────────────────────────────

function CourierRow({
  item,
  isManager,
  showActions,
  currentUserId,
  onOrder,
  onReject,
  onMarkReceived,
  onReopen,
  onCancel,
}: CourierItemProps & { showActions: boolean }) {
  const isPending = item.status === "pending";
  const isRequester = item.requested_by === currentUserId;
  const canMarkReceived = item.status === "ordered" && !item.is_received && (isRequester || isManager);

  const StatusIcon = () => {
    if (item.status === "ordered") return <CheckCircle2 size={12} className="text-[var(--color-primary)]" />;
    if (item.status === "rejected") return <XCircle size={12} className="text-red-500" />;
    if (item.status === "cancelled") return <Ban size={12} className="text-gray-400" />;
    return <Clock size={12} className="text-yellow-500" />;
  };

  return (
    <tr
      className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors border-l-4 ${
        isPending ? "border-yellow-400 dark:border-yellow-500" : "border-transparent"
      }`}
    >
      <td className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
        {formatDate(item.submitted_at)}
        {item.requested_at && (
          <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
            od {formatDate(item.requested_at)}
          </p>
        )}
      </td>

      <td className="px-3 py-2.5">
        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
          item.delivery_type === "courier"
            ? "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300"
            : "bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300"
        }`}>
          {item.delivery_type === "courier" ? <Truck size={10} /> : <Package size={10} />}
          {item.delivery_type_display}
        </span>
      </td>

      <td className="px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300">
        {item.pickup_address}
      </td>

      <td className="px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300">
        <span>{item.recipient_address}</span>
        {item.notes && (
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 italic">{item.notes}</p>
        )}
      </td>

      <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-400">{item.size}</td>

      <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
        {item.requested_by_name ?? "—"}
      </td>

      <td className="px-3 py-2.5">
        <Badge color={COURIER_STATUS_COLORS[item.status]}>
          <StatusIcon />
          <span className="ml-1">{item.status_display}</span>
        </Badge>
      </td>

      <td className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
        {formatDate(item.ordered_at)}
        {item.ordered_by_name && (
          <p className="text-gray-400 dark:text-gray-500 text-xs">{item.ordered_by_name}</p>
        )}
        {item.label_file_url && (
          <a
            href={item.label_file_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-0.5 text-[var(--color-primary)] hover:underline text-xs mt-0.5"
          >
            <Paperclip size={10} /> Etykieta
          </a>
        )}
      </td>

      <td className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
        {formatDate(item.ordered_for)}
      </td>

      <td className="px-3 py-2.5 text-center">
        {item.is_received ? (
          <span title={item.received_at ? formatDate(item.received_at) : undefined} className="inline-flex items-center justify-center text-green-600 dark:text-green-400">
            <CheckCircle2 size={16} />
          </span>
        ) : canMarkReceived ? (
          <button
            onClick={() => onMarkReceived(item)}
            className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700 font-medium whitespace-nowrap"
          >
            Odebrana
          </button>
        ) : (
          <span className="text-gray-300 dark:text-gray-600">—</span>
        )}
      </td>

      {showActions && (
        <td className="px-3 py-2.5">
          <div className="flex items-center gap-1.5 flex-wrap">
            {isManager && item.status === "pending" && (
              <>
                <button
                  onClick={() => onOrder(item)}
                  className="text-xs bg-[var(--color-primary)] text-white px-2 py-1 rounded hover:bg-[var(--color-primary-hover)] font-medium whitespace-nowrap"
                >
                  Zamów
                </button>
                <button
                  onClick={() => onReject(item)}
                  className="text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 px-2 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-600 font-medium"
                >
                  Odrzuć
                </button>
              </>
            )}
            {isRequester && item.status === "pending" && (
              <button
                onClick={() => onCancel(item)}
                className="text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 px-2 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 hover:border-red-300 font-medium flex items-center gap-1 whitespace-nowrap"
              >
                <Ban size={11} /> Anuluj
              </button>
            )}
            {isManager && item.status === "rejected" && (
              <button
                onClick={() => onReopen(item)}
                className="text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 px-2 py-1 rounded hover:bg-gray-50 font-medium flex items-center gap-1 whitespace-nowrap"
              >
                <RotateCcw size={11} /> Przywróć
              </button>
            )}
          </div>
        </td>
      )}
    </tr>
  );
}

// ── Mobile card ───────────────────────────────────────────────────────────────

function CourierCard({
  item,
  isManager,
  currentUserId,
  onOrder,
  onReject,
  onMarkReceived,
  onReopen,
  onCancel,
}: CourierItemProps) {
  const isPending = item.status === "pending";
  const isRequester = item.requested_by === currentUserId;
  const canMarkReceived = item.status === "ordered" && !item.is_received && (isRequester || isManager);
  const canCancel = item.status === "pending" && isRequester;

  const StatusIcon = () => {
    if (item.status === "ordered") return <CheckCircle2 size={12} className="text-[var(--color-primary)]" />;
    if (item.status === "rejected") return <XCircle size={12} className="text-red-500" />;
    if (item.status === "cancelled") return <Ban size={12} className="text-gray-400" />;
    return <Clock size={12} className="text-yellow-500" />;
  };

  const submittedDate = formatDate(item.submitted_at);

  const hasActions =
    (isManager && (item.status === "pending" || item.status === "rejected")) || canMarkReceived || canCancel;

  return (
    <li
      className={`relative rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 shadow-sm border-l-4 ${
        isPending ? "border-l-yellow-400 dark:border-l-yellow-500" : "border-l-gray-200 dark:border-l-gray-700"
      }`}
    >
      {/* Top row */}
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <Badge color={COURIER_STATUS_COLORS[item.status]}>
            <StatusIcon />
            <span className="ml-1">{item.status_display}</span>
          </Badge>
        </div>
        <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
          item.delivery_type === "courier"
            ? "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300"
            : "bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300"
        }`}>
          {item.delivery_type === "courier" ? <Truck size={10} /> : <Package size={10} />}
          {item.delivery_type_display}
        </span>
      </div>

      {/* Addresses */}
      <div className="mb-1">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          <span className="font-medium text-gray-700 dark:text-gray-300">Nadanie:</span> {item.pickup_address}
        </p>
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-0.5 break-words">
          {item.recipient_address}
        </p>
      </div>

      {item.notes && (
        <p className="text-xs text-gray-500 dark:text-gray-400 italic mb-1.5 line-clamp-2">{item.notes}</p>
      )}

      {/* Meta */}
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400 mt-1">
        <span>📅 {submittedDate}</span>
        {item.requested_at && (
          <span>od {formatDate(item.requested_at)}</span>
        )}
        <span>Rozmiar: {item.size}</span>
        <span>{item.requested_by_name ?? "—"}</span>
        {item.ordered_at && (
          <span>
            Zam: {formatDate(item.ordered_at)}
            {item.ordered_by_name && <span className="text-gray-400"> · {item.ordered_by_name}</span>}
          </span>
        )}
        {item.ordered_for && (
          <span>
            Na: {formatDate(item.ordered_for)}
          </span>
        )}
        {item.label_file_url && (
          <a
            href={item.label_file_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-0.5 text-[var(--color-primary)] hover:underline"
          >
            <Paperclip size={10} /> Etykieta
          </a>
        )}
        {item.is_received && (
          <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400 font-medium">
            <CheckCircle2 size={12} /> Odebrana
          </span>
        )}
      </div>

      {/* Actions */}
      {hasActions && (
        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
          {isManager && item.status === "pending" && (
            <>
              <button
                onClick={() => onOrder(item)}
                className="flex-1 text-sm bg-[var(--color-primary)] text-white px-3 py-2 rounded hover:bg-[var(--color-primary-hover)] font-medium"
              >
                Zamów
              </button>
              <button
                onClick={() => onReject(item)}
                className="text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-3 py-2 rounded hover:bg-gray-50 dark:hover:bg-gray-600 font-medium"
              >
                Odrzuć
              </button>
            </>
          )}
          {canMarkReceived && (
            <button
              onClick={() => onMarkReceived(item)}
              className="flex-1 text-sm bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700 font-medium flex items-center justify-center gap-1.5"
            >
              <CheckCircle2 size={13} /> Odebrana
            </button>
          )}
          {canCancel && (
            <button
              onClick={() => onCancel(item)}
              className="text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-3 py-2 rounded hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 font-medium flex items-center justify-center gap-1.5"
            >
              <Ban size={13} /> Anuluj
            </button>
          )}
          {isManager && item.status === "rejected" && (
            <button
              onClick={() => onReopen(item)}
              className="flex-1 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-3 py-2 rounded hover:bg-gray-50 dark:hover:bg-gray-600 font-medium flex items-center justify-center gap-1.5"
            >
              <RotateCcw size={13} /> Przywróć
            </button>
          )}
        </div>
      )}
    </li>
  );
}
